/**
 * Every anchor on the STO details page, in one flat namespace.
 *
 * Hierarchy lives here, never in the URL — `#risks-credit-risk` is a single
 * fragment whose registry entry knows which section it belongs to and what
 * opening it does. See docs/STO-ARCHITECTURE.md §7.
 */

export const STO_SECTION_IDS = [
  'hero',
  'key-actions',
  'overview',
  'how-to-invest',
  'support',
  'key-information',
  'performance',
  'dividends',
  'documents',
  'risks',
  'terms',
  'trading',
  'updates',
  'borrow',
] as const

export type StoSectionId = (typeof STO_SECTION_IDS)[number]

/** `scroll` = plain section · `expand` = accordion row or metric panel · `tab` = switch a tab pair. */
export type AnchorBehaviour = 'scroll' | 'expand' | 'tab'

export interface StoAnchor {
  id: string
  section: StoSectionId
  behaviour: AnchorBehaviour
}

/**
 * Metric anchors are `metric-<key>` where the key is authored per project, so they
 * cannot be listed here. The prefix is reserved instead: no section id may start with
 * it, which is what keeps author-defined keys from ever colliding with a section.
 */
export const METRIC_ANCHOR_PREFIX = 'metric-'

/** `#metric-<key>` — only for metrics whose author set a key. */
export const metricAnchor = (key: string): string => `${METRIC_ANCHOR_PREFIX}${key}`

const isMetricAnchorId = (id: string): boolean =>
  id.startsWith(METRIC_ANCHOR_PREFIX) && id.length > METRIC_ANCHOR_PREFIX.length

const sectionAnchors: StoAnchor[] = STO_SECTION_IDS.map((section) => ({
  id: section,
  section,
  behaviour: 'scroll',
}))

/**
 * Anchors below the section level. Metric anchors (`metric-<key>`) are author-defined
 * in back-office, so they are resolved by prefix in `findAnchor` rather than listed here.
 */
const nestedAnchors: StoAnchor[] = [
  { id: 'trading-secondary-market', section: 'trading', behaviour: 'expand' },
  { id: 'trading-order-book', section: 'trading', behaviour: 'expand' },
  { id: 'performance-chart', section: 'performance', behaviour: 'tab' },
  { id: 'performance-table', section: 'performance', behaviour: 'tab' },
  { id: 'dividends-history', section: 'dividends', behaviour: 'tab' },
]

export const STO_ANCHORS: readonly StoAnchor[] = [...sectionAnchors, ...nestedAnchors]

const anchorsById = new Map(STO_ANCHORS.map((anchor) => [anchor.id, anchor]))

export const findAnchor = (id: string | undefined | null): StoAnchor | undefined => {
  if (!id) return undefined
  const clean = id.replace(/^#/, '')

  // Metric anchors (hero metrics and key terms) open their own panel, so the behaviour is
  // known without a registry entry. Neither section collapses, so `section` is nominal.
  if (isMetricAnchorId(clean)) return { id: clean, section: 'hero', behaviour: 'expand' }

  return anchorsById.get(clean)
}

/**
 * Legacy `/:slug/<tab>` URLs must still land on the right block. Tabs whose content
 * was removed map to the page top (empty string) rather than 404.
 */
export const LEGACY_TAB_TO_ANCHOR: Record<string, string> = {
  pitch: 'overview',
  terms: 'terms',
  trading: 'trading',
  performance: 'performance',
  borrow: 'borrow',
  portfolio: '',
  team: '',
  partners: '',
  updates: 'updates',
  comments: '',
}

/**
 * `/:slug/trading` -> `/:slug#trading`. Unknown tabs fall back to the page top. `search`
 * (`?utm_source=…`) is kept, as the server's 301 keeps it.
 */
export const legacyTabToPath = (slug: string, tab: string, search = ''): string => {
  const anchor = LEGACY_TAB_TO_ANCHOR[tab?.toLowerCase()] ?? ''
  return `/${slug}${search}${anchor ? `#${anchor}` : ''}`
}
